import {
  BRIDGE_DEMO,
  BRIDGE_DEVELOPMENT,
  BRIDGE_PRODUCTION,
  BridgeEnv,
  ERROR_CODE_400,
  ERROR_CODE_401,
  ERROR_CODE_404,
  ERROR_CODE_500,
  ErrorCode,
  TRANSFER_FLOW_IN,
  TRANSFER_FLOW_OUT,
  TRANSFER_FLOW_STATUS,
  TransferFlow,
} from "../typings";

import generateErrorMessage from "./generateErrorMessage";
import validateClient from "./validateClient";
import {
  sampleTransferIntentInResponseIra,
  sampleTransferIntentInResponseEmployerSponsored,
  randomScenario,
  sampleTransferIntentOutResponseIra,
  sampleTransferIntentOutResponseIra2,
} from "../api/mockAPIResponse";
import { TransferIntentResponse } from "../class/transferIntent";

import { logSentryUser } from "../lib/sentry";
import { TransferInstruction } from "../class/transferInstruction";
import { sampleTransferInstructions } from "./__test__/test-data";
import axios from "axios";

const getTransferFlowForBridge = ({ transferIntentId }: { transferIntentId?: string }): TransferFlow => {
  if (transferIntentId && transferIntentId !== "undefined") {
    return transferIntentId.split("--").length > 0
      ? transferIntentId.split("--")[1].toLowerCase().includes("in")
        ? TRANSFER_FLOW_IN
        : transferIntentId.split("--")[1].toLowerCase().includes("out")
          ? TRANSFER_FLOW_OUT
          : TRANSFER_FLOW_STATUS
      : TRANSFER_FLOW_IN;
  } else {
    return TRANSFER_FLOW_STATUS;
  }
};

const getRandomScenarioForTransferOut = (): TransferIntentResponse => {
  const scenario = randomScenario();
  return scenario === 0 ? sampleTransferIntentOutResponseIra : sampleTransferIntentOutResponseIra2;
};

export type AuthenticationRequestParams = {
  accessToken: string;
  clientId: string;
  environment: string;
  transferIntentId: string | "undefined";
  userId: string | "undefined";
};

export type AuthenticationRes = {
  ready: boolean;
  env?: BridgeEnv;
  errorCode?: ErrorCode;
  errorMessage?: string;
  data?: {
    transferIntentDetails?: TransferIntentResponse;
    transferInstructionDetails?: TransferInstruction[];
  };
  transferFlow?: TransferFlow;
  clientName?: string;
};
// TODO: Unit TEST
const authenticate = async (params: AuthenticationRequestParams): Promise<AuthenticationRes> => {
  const { accessToken, clientId, environment = "development", transferIntentId, userId } = params;

  const env: BridgeEnv = environment
    ? environment.toLowerCase() === "production"
      ? BRIDGE_PRODUCTION
      : BRIDGE_DEVELOPMENT
    : BRIDGE_DEVELOPMENT;

  // CHECK all params
  if (!accessToken || accessToken === "undefined" || !clientId || clientId === "undefined") {
    return {
      ready: false,
      errorCode: ERROR_CODE_400,
      errorMessage: generateErrorMessage({
        errorCode: ERROR_CODE_400,
        params: "access token and client id",
      }),
    };
  }
  if ((!transferIntentId || transferIntentId === "undefined") && (!userId || userId === "undefined")) {
    return {
      ready: false,
      errorCode: ERROR_CODE_400,
      errorMessage: generateErrorMessage({
        errorCode: ERROR_CODE_400,
        params: "transferIntentId or userId",
      }),
    };
  }

  if (accessToken.includes("bridge")) {
    return {
      clientName: "InvestCo",
      ready: true,
      env: BRIDGE_DEMO,
      transferFlow: getTransferFlowForBridge({ transferIntentId }),
      data: {
        transferIntentDetails:
          getTransferFlowForBridge({ transferIntentId }) === "TRANSFER_FLOW_OUT"
            ? getRandomScenarioForTransferOut()
            : randomScenario() === 0
              ? sampleTransferIntentInResponseIra
              : sampleTransferIntentInResponseEmployerSponsored,
        transferInstructionDetails: userId && userId !== "undefined" ? sampleTransferInstructions : [],
      },
    };
  }

  // Authenticate API
  const api = axios.create({
    baseURL: env === BRIDGE_PRODUCTION ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_API_BASE_URL,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ClientId: clientId,
      "X-Auth-Dispatch": "EXTERNAL",
    },
  });
  let clientName, transferFlow, transferIntentDetails, transferInstructionDetails;

  try {
    const validateClientFunc = async () =>
      await validateClient({
        clientId,
        accessToken,
        environment,
      }).then((res) => res.clientName);
    let allFuncs: Record<string, () => Promise<any>> = {
      clientName: validateClientFunc,
    };
    if (transferIntentId && transferIntentId !== "undefined" && transferIntentId !== "null") {
      const transferIntentIdFunc = async () =>
        await api({
          method: "GET",
          url: `/transferIntent?transferIntentId=${transferIntentId}`,
        }).then((e) => e.data.data);
      allFuncs = {
        ...allFuncs,
        transferIntentDetails: transferIntentIdFunc,
      };
    }
    if (userId && userId !== "undefined" && userId !== "null") {
      const transferInstructionDetailsFunc = async () =>
        await api({
          method: "GET",
          url: `/api/userIdentity/${userId}/transfer/transferInstruction`,
        }).then((e) => e.data.data);
      allFuncs = {
        ...allFuncs,
        transferInstructionDetails: transferInstructionDetailsFunc,
      };
    }
    const res = await Promise.all(
      Object.entries(allFuncs).map(async ([label, value]) => {
        return {
          [label]: await value(),
        };
      }),
    );
    res.forEach((result) => {
      if ("clientName" in result) {
        clientName = result.clientName;
      }
      if ("transferIntentDetails" in result) {
        transferIntentDetails = result.transferIntentDetails;
      }
      if ("transferInstructionDetails" in result) {
        transferInstructionDetails = result.transferInstructionDetails;
      }
    });

    logSentryUser({ clientId, clientName: clientName || "", environment });
  } catch (e: any) {
    const errorCode = e?.message.includes("404")
      ? ERROR_CODE_404
      : e?.message.includes("401") || e?.message.includes("403")
        ? ERROR_CODE_401
        : ERROR_CODE_500;

    return {
      ready: false,
      errorCode,
      errorMessage:
        errorCode === ERROR_CODE_401
          ? "Wrong credentials"
          : errorCode === "ERROR_CODE_404"
            ? `${transferIntentId && transferIntentId !== "undefined" ? "Transfer Intent Id" : "User Id"} not found`
            : "Unable to authenticate",
    };
  }

  return {
    clientName,
    ready: true,
    transferFlow,
    data: {
      transferIntentDetails,
      transferInstructionDetails,
    },
    env,
  };
};

export default authenticate;
